import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchDocs } from "../config/docsIndex";

/**
 * SearchBar Component
 * 
 * A reusable search component with:
 * - Real-time filtering
 * - Keyboard navigation (↑ ↓ Enter Esc)
 * - Click-outside detection
 * - Smooth navigation with section scrolling
 * 
 * Props:
 * - placeholder: Input placeholder text (default: "Search docs…")
 * - className: Additional CSS classes for the container
 * - inputClassName: Additional CSS classes for the input
 */
export default function SearchBar({ 
  placeholder = "Search docs…",
  className = "",
  inputClassName = ""
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Search logic - triggered on query change
  useEffect(() => {
    if (query.trim() === "") {
      setResults([]);
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }

    const searchResults = searchDocs(query);
    setResults(searchResults);
    setIsOpen(true);
    setSelectedIndex(-1);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigate to selected result
  const navigateToResult = (result) => {
    const targetPath = result.path;
    const targetHash = result.sectionId ? `#${result.sectionId}` : "";

    // Close search and clear
    setIsOpen(false);
    setQuery("");
    setResults([]);

    // Navigate
    navigate(`${targetPath}${targetHash}`);

    // Scroll to section if hash exists
    if (result.sectionId) {
      setTimeout(() => {
        const element = document.getElementById(result.sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          navigateToResult(results[selectedIndex]);
        }
        break;

      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;

      default:
        break;
    }
  };

  // Highlight matching text in result title
  const highlightMatch = (text, query) => {
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 text-sm
                     focus:outline-none focus:ring-2 focus:ring-[#C9A24E] ${inputClassName}`}
        />
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          {results.length > 0 ? (
            <ul className="py-2">
              {results.map((result, index) => (
                <li
                  key={`${result.path}-${result.sectionId || "main"}`}
                  onClick={() => navigateToResult(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    selectedIndex === index
                      ? "bg-[#C9A24E]/10"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Search className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm">
                        {highlightMatch(result.title, query)}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {result.category}
                        {result.sectionId && (
                          <span className="ml-1">• Section</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">
              <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p>No results found for "{query}"</p>
              <p className="text-xs mt-1">Try different keywords</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
